import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { contracts, deployments, inventory } from "@/api/endpoints";
import { DeploymentPicker } from "@/components/shared/DeploymentPicker";
import { Spinner } from "@/components/shared/Spinner";
import { ContractCredits } from "./ContractCredits";
import { useToast } from "@/components/shared/Toast";
import { useAuthStore } from "@/store/authStore";
import type { BillingFrequency, Contract, ContractStatus } from "@/types/api";

interface FormState {
  customer_name: string;
  start_date: string;
  end_date: string;
  billing_amount: string;
  billing_frequency: BillingFrequency;
  payment_terms: string;
  status: ContractStatus;
  notes: string;
  document_url: string;
  netbox_device_ids: number[];
  tempo_deployment_id: string;
}

const TODAY = new Date().toISOString().slice(0, 10);

const EMPTY: FormState = {
  customer_name: "",
  start_date: TODAY,
  end_date: TODAY,
  billing_amount: "0",
  billing_frequency: "monthly",
  payment_terms: "net_30",
  status: "pending",
  notes: "",
  document_url: "",
  netbox_device_ids: [],
  tempo_deployment_id: "",
};

export function ContractEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const nav = useNavigate();
  const qc = useQueryClient();
  const showToast = useToast((s) => s.show);
  const isAdmin = useAuthStore((s) => s.user?.role) === "admin";

  const inv = useQuery({
    queryKey: ["inventory", "for-picker"],
    queryFn: () => inventory.list({}),
  });

  const existing = useQuery({
    queryKey: ["contracts", id],
    queryFn: () => contracts.get(id!),
    enabled: !isNew && !!id,
  });

  // Assigning servers to a Tempo deployment is admin-only on the backend.
  const deps = useQuery({
    queryKey: ["deployments", "list"],
    queryFn: () => deployments.list(),
    enabled: isNew && isAdmin,
    staleTime: 60_000,
  });

  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (existing.data) {
      const c = existing.data;
      setForm({
        customer_name: c.customer_name,
        start_date: c.start_date,
        end_date: c.end_date,
        billing_amount: c.billing_amount,
        billing_frequency: c.billing_frequency,
        payment_terms: c.payment_terms,
        status: c.status,
        notes: c.notes ?? "",
        document_url: c.document_url ?? "",
        netbox_device_ids: c.netbox_device_ids,
        tempo_deployment_id: "",
      });
    }
  }, [existing.data]);

  const create = useMutation({
    mutationFn: () =>
      contracts.create({
        ...(form as unknown as Partial<Contract> & { netbox_device_ids: number[] }),
        tempo_deployment_id: form.tempo_deployment_id || null,
      }),
    onSuccess: (c) => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      const a = c.deployment_assignment;
      if (a) {
        qc.invalidateQueries({ queryKey: ["inventory"] });
        qc.invalidateQueries({ queryKey: ["deployments"] });
        qc.invalidateQueries({ queryKey: ["revenue"] });
      }
      if (a && a.failed.length > 0) {
        const failed = a.failed.map((f) => `device ${f.device_id}: ${f.error}`).join("; ");
        showToast(
          `Contract created, but ${a.failed.length} server(s) couldn't be assigned to the deployment (${failed}). Retry from Inventory.`,
          "error",
        );
      } else {
        showToast(
          a ? `Contract created; ${a.succeeded.length} server(s) assigned to deployment.` : "Contract created.",
          "success",
        );
      }
      nav(`/contracts/${c.id}`);
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "create failed", "error"),
  });

  const update = useMutation({
    mutationFn: () =>
      contracts.update(id!, {
        customer_name: form.customer_name,
        start_date: form.start_date,
        end_date: form.end_date,
        billing_amount: form.billing_amount,
        billing_frequency: form.billing_frequency,
        payment_terms: form.payment_terms,
        status: form.status,
        notes: form.notes,
        document_url: form.document_url,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      showToast("Contract updated.", "success");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "update failed", "error"),
  });

  const cancel = useMutation({
    mutationFn: () => contracts.cancel(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      showToast("Contract cancelled.", "success");
      nav("/contracts");
    },
  });

  const remove = useMutation({
    mutationFn: () => contracts.deletePermanently(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["contracts"] });
      qc.invalidateQueries({ queryKey: ["revenue"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      showToast("Contract deleted.", "success");
      nav("/contracts");
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "delete failed", "error"),
  });

  const filteredDevices = useMemo(() => inv.data ?? [], [inv.data]);

  // Selected servers already mapped to a different Tempo deployment.
  const movingServers = useMemo(
    () =>
      form.tempo_deployment_id
        ? filteredDevices.filter(
            (d) =>
              form.netbox_device_ids.includes(d.netbox_id) &&
              d.tempo_deployment_xref &&
              d.tempo_deployment_xref !== form.tempo_deployment_id,
          )
        : [],
    [filteredDevices, form.netbox_device_ids, form.tempo_deployment_id],
  );

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isNew) create.mutate();
    else update.mutate();
  };

  if (!isNew && existing.isLoading) return <Spinner />;

  return (
    <div className="space-y-10">
      <form onSubmit={submit} className="space-y-5 max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-semibold">
            {isNew ? "New contract" : "Edit contract"}
          </h1>
          {!isNew && (
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => cancel.mutate()}
                className="text-sm text-red-600 hover:underline"
              >
                Cancel contract
              </button>
              {isAdmin && (
                <button
                  type="button"
                  onClick={() => {
                    if (
                      confirm(
                        `Permanently delete the contract for ${form.customer_name}? ` +
                          "This removes it, its server links and its credits, and erases its revenue " +
                          "from all historical reports. This cannot be undone.",
                      )
                    )
                      remove.mutate();
                  }}
                  disabled={remove.isPending}
                  className="rounded-md border border-red-300 px-2 py-1 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-900 dark:hover:bg-red-950/40"
                >
                  Delete permanently
                </button>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Customer">
            <input
              value={form.customer_name}
              onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
              required
              className="input"
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as ContractStatus })}
              className="input"
            >
              <option value="active">active</option>
              <option value="pending">pending</option>
              <option value="expired">expired</option>
              <option value="cancelled">cancelled</option>
            </select>
          </Field>
          <Field label="Start date">
            <input
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              required
              className="input"
            />
          </Field>
          <Field label="End date">
            <input
              type="date"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              required
              className="input"
            />
          </Field>
          <Field label="Billing amount (USD)">
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.billing_amount}
              onChange={(e) => setForm({ ...form, billing_amount: e.target.value })}
              required
              className="input"
            />
          </Field>
          <Field label="Billing frequency">
            <select
              value={form.billing_frequency}
              onChange={(e) =>
                setForm({ ...form, billing_frequency: e.target.value as BillingFrequency })
              }
              className="input"
            >
              <option value="monthly">monthly</option>
              <option value="quarterly">quarterly</option>
              <option value="annual">annual</option>
              <option value="one_time">one-time</option>
            </select>
          </Field>
          <Field label="Payment terms">
            <input
              value={form.payment_terms}
              onChange={(e) => setForm({ ...form, payment_terms: e.target.value })}
              className="input"
            />
          </Field>
          <Field label="Document URL (optional)">
            <input
              value={form.document_url}
              onChange={(e) => setForm({ ...form, document_url: e.target.value })}
              className="input"
              placeholder="https://…"
            />
          </Field>
        </div>

        <Field label="Notes">
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="input"
          />
        </Field>

        {isNew && (
          <Field label="Servers covered (multi-select)">
            <select
              multiple
              value={form.netbox_device_ids.map(String)}
              onChange={(e) =>
                setForm({
                  ...form,
                  netbox_device_ids: Array.from(e.target.selectedOptions).map((o) => Number(o.value)),
                })
              }
              className="input min-h-[8rem]"
            >
              {filteredDevices.map((d) => (
                <option key={d.netbox_id} value={d.netbox_id}>
                  {d.name} — {d.gpu_type ?? d.device_type ?? "?"} ({d.tenant ?? "unassigned"})
                </option>
              ))}
            </select>
            <span className="text-xs text-slate-500">
              For existing contracts, manage covered servers from the contract detail page (TODO).
            </span>
          </Field>
        )}

        {isNew && isAdmin && (
          <div className="block text-sm">
            <span className="text-slate-600 dark:text-slate-300">Tempo deployment (optional)</span>
            <div className="mt-1">
              <DeploymentPicker
                deployments={deps.data ?? []}
                value={form.tempo_deployment_id}
                onChange={(tempo_deployment_id) => setForm({ ...form, tempo_deployment_id })}
                disabled={deps.isLoading || !!deps.error}
                placeholder={
                  deps.isLoading ? "Loading deployments…" : "Optional — search by name or paste a Tempo deployment ID"
                }
              />
              <span className="block text-xs text-slate-500">
                {deps.error
                  ? `Tempo unavailable: ${deps.error instanceof Error ? deps.error.message : "unknown error"}`
                  : "Also assigns the servers selected above to this deployment in Netbox."}
              </span>
              {movingServers.length > 0 && (
                <span className="block text-xs text-amber-700 dark:text-amber-400">
                  {movingServers.length} selected server(s) will be moved off their current deployment:{" "}
                  {movingServers.map((d) => d.name).join(", ")}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={create.isPending || update.isPending}
            className="rounded-md bg-brand-500 px-3 py-2 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {isNew ? "Create" : "Save"}
          </button>
          <button
            type="button"
            onClick={() => nav("/contracts")}
            className="rounded-md px-3 py-2 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Back
          </button>
        </div>

        <style>{`
          .input {
            width: 100%;
            border-radius: 0.375rem;
            border: 1px solid rgb(203 213 225);
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            background: white;
          }
          @media (prefers-color-scheme: dark) {
            .input { background: rgb(30 41 59); border-color: rgb(51 65 85); color: white; }
          }
        `}</style>
      </form>
      {!isNew && id && <ContractCredits contractId={id} />}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block text-sm">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
